import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';
import { Country, CountryChangeHistory } from '../../types/asset.types';
import { ApiEndpoints } from '../../services/ApiUrls';


// Pagination interface for countries
export interface CountryPaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Country[];
}

// Define types for request and response
export interface CountryCreateInput {
  name: string;
  code: string;
  isActive?: boolean;
}

export interface CountryUpdateInput extends Partial<CountryCreateInput> {
  updateReason?: string;
}

export interface CountryFilter {
  search?: string;
  isActive?: boolean;
  dropdown?: boolean;
}

// Country history data interface
export interface CountryHistoryData {
  id: string;
  name: string;
  changes: CountryChangeHistory[];
}

// Create API
export const countryApi = createApi({
  reducerPath: 'countryApi',
  baseQuery: createExtendedTokenBaseQuery(''),
  tagTypes: ['Country', 'CountryHistory'],
  endpoints: (builder) => ({
    getCountries: builder.query<CountryPaginatedResponse, CountryFilter | void>({
      query: (filter = {}) => {
        const params = new URLSearchParams();
        params.append('paginate', 'false');
        if (filter && 'dropdown' in filter && filter.dropdown) {
          params.append('dropdown', 'true');
        }
        if (filter) {
          if ('search' in filter && filter.search) params.append('search', filter.search);
          if ('isActive' in filter && filter.isActive !== undefined)
            params.append('is_active', filter.isActive.toString());
        }
        return { url: ApiEndpoints.countries.list, params };
      },
      providesTags: ['Country'],
    }),

    getCountryById: builder.query<Country, string>({
      query: (id) => ApiEndpoints.countries.detail(id),
      providesTags: (result, error, id) => [{ type: 'Country', id }],
    }),

    createCountry: builder.mutation<Country, CountryCreateInput>({
      query: (country) => ({
        url: ApiEndpoints.countries.list,
        method: 'POST',
        body: country,
      }),
      invalidatesTags: ['Country'],
    }),

    updateCountry: builder.mutation<Country, { id: string; country: CountryUpdateInput }>({
      query: ({ id, country }) => ({
        url: ApiEndpoints.countries.detail(id),
        method: 'PATCH',
        body: country,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Country', id }, 'Country'],
    }),

    getCountryHistory: builder.query<CountryHistoryData, string>({
      query: (id) => ApiEndpoints.countries.history(id),
      providesTags: (result, error, id) => [{ type: 'CountryHistory', id }],
    }),
  }),
});


// Export hooks
export const {
  useGetCountriesQuery,
  useGetCountryByIdQuery,
  useCreateCountryMutation,
  useUpdateCountryMutation,
  useGetCountryHistoryQuery,
} = countryApi;